import { TenantFormattingService } from '../../../../core/services/tenant-formatting.service';
import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgStyle } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { getApiErrorMessage } from '../../../../core/utils/api-error-message';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faArrowsLeftRight, faBroom, faBullseye, faCheck, faCircleQuestion, faChevronDown, faChevronUp, faDownload, faEraser, faFloppyDisk, faLayerGroup, faPaintBrush, faPlus, faRotateLeft, faRotateRight, faTooth, faTrash, faTriangleExclamation, faUpload, faXmark } from '@fortawesome/free-solid-svg-icons';
import { DentalArchesComponent, ToothSelectionEvent } from '../../../components/dental-arches.component/dental-arches.component';
import { LoadingSpinnerComponent } from '../../../components/loading-spinner.component/loading-spinner.component';
import { NoItemsFoundComponent } from '../../../components/no-items-found/no-items-found.component';
import { ToothColors } from '../../../../core/models/shared/tooth-colors.enum';
import { OrdersService } from '../../../../core/services/orders.service';
import { FilesService } from '../../../../core/services/files.service';
import { ClientOrderProductModel } from '../../../../core/models/client-order-product.model';
import { ProductModel } from '../../../../core/models/product.model';
import { CreateOrderRequest } from '../../../../core/models/create-order-request.model';
import { OrderModel } from '../../../../core/models/order.model';
import { CreateFilesComponent } from '../../files/create-files/create-files.component';
import { PendingChangesComponent } from '../../../../core/guards/pending-changes.guard';
import { ErrorAlertComponent } from '../../../components/error-alert.component/error-alert.component';
import { FileModel, PendingFileUpload } from '../../../../core/models/file.model';
import { MAX_FILE_UPLOAD_SIZE_BYTES } from '../../../../core/constants/file-upload.constants';
import { AppDatePipe } from '../../../pipes/app-date.pipe';
interface ProductCategoryGroup {
  name: string;
  color: string;
  products: ClientOrderProductModel[];
}
interface SelectedOrderProduct {
  id: string;
  name: string;
  color: string;
  teeth: number[];
  quantity: number;
  isFullConstruction: boolean;
}
interface OrderSaveResult {
  order: OrderModel;
  failedUploadCount: number;
  uploadedCount: number;
  failureMessages: string[];
}
interface OrderEditorSnapshot {
  selectedProducts: SelectedOrderProduct[];
  currentProduct: SelectedOrderProduct | null;
  currentTooth: number | null;
  currentToothCount: number | null;
  selectionMode: SelectionMode;
}
type SelectionMode = 'choose' | 'single' | 'range' | 'clear';
const TOOTH_ORDER_BY_COUNT = [
  18, 17, 16, 15, 14, 13, 12, 11,
  21, 22, 23, 24, 25, 26, 27, 28,
  38, 37, 36, 35, 34, 33, 32, 31,
  41, 42, 43, 44, 45, 46, 47, 48
];
const TOOTH_COUNT_BY_TOOTH = new Map<number, number>(TOOTH_ORDER_BY_COUNT.map((tooth, index) => [tooth, index + 1]));
const MAX_HISTORY_LENGTH = 100;
@Component({
  selector: 'app-create-order',
  imports: [
    FormsModule,
    NgStyle,
    MatTooltipModule,
    RouterLink,
    FaIconComponent,
    DentalArchesComponent,
    LoadingSpinnerComponent,
    NoItemsFoundComponent,
    CreateFilesComponent,
    ErrorAlertComponent,
    AppDatePipe
  ],
  templateUrl: './create-order.component.html',
  styleUrl: './create-order.component.css'
})
export class CreateOrderComponent implements OnInit, AfterViewChecked, OnDestroy, PendingChangesComponent {
  readonly formatting = inject(TenantFormattingService);
  ToothColors = ToothColors;
  availableProducts: ClientOrderProductModel[] = [];
  loadingProducts = false;
  creatingOrder = false;
  createOrderError = '';
  currentTooth: number | null = null;
  currentToothCount: number | null = null;
  currentProduct: SelectedOrderProduct | null = null;
  selectionMode: SelectionMode = 'choose';
  selectedProducts: SelectedOrderProduct[] = [];
  selectedDentalProducts: ProductModel[] = [];
  selectedFiles: PendingFileUpload[] = [];
  existingFiles: FileModel[] = [];
  downloadingFileIds = new Set<string>();
  createOrderForm: {
    patientName: string;
    toothColor: ToothColors | null;
    description: string;
  } = {
      patientName: '',
      toothColor: null,
      description: ''
    };
  private readonly leaveConfirmationMessage = 'Дали сте сигурни дека сакате да ја напуштите страницата? Незачуваните промени ќе се изгубат.';
  private orderSaved = false;
  private readonly popoverInstances = new Map<Element, any>();
  private persistedOrderId: string | null = null;
  private lastSavedRequestSignature: string | null = null;
  private undoStack: OrderEditorSnapshot[] = [];
  private redoStack: OrderEditorSnapshot[] = [];
  private historyActionDepth = 0;
  editingOrderId: string | null = null;
  loadingOrder = false;
  orderLoadFailed = false;
  orderLoadSucceeded = false;
  readonly toothColorOptions = Object.keys(ToothColors)
    .filter(key => Number.isNaN(Number(key)))
    .map(key => ({
      label: key,
      value: ToothColors[key as keyof typeof ToothColors] as ToothColors
    }));
  faArrowLeft = faArrowLeft;
  faArrowsLeftRight = faArrowsLeftRight;
  faBroom = faBroom;
  faBullseye = faBullseye;
  faCheck = faCheck;
  faCircleQuestion = faCircleQuestion;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faDownload = faDownload;
  faEraser = faEraser;
  faFloppyDisk = faFloppyDisk;
  faLayerGroup = faLayerGroup;
  faPaintBrush = faPaintBrush;
  faPlus = faPlus;
  faRotateLeft = faRotateLeft;
  faRotateRight = faRotateRight;
  faTooth = faTooth;
  faTrash = faTrash;
  faTriangleExclamation = faTriangleExclamation;
  faUpload = faUpload;
  faXmark = faXmark;
  constructor(private orderService: OrdersService, private filesService: FilesService, private router: Router, private route: ActivatedRoute, private elementRef: ElementRef<HTMLElement>) { }
  ngOnInit(): void {
    this.editingOrderId = this.route.snapshot.paramMap.get('id');
    this.persistedOrderId = this.editingOrderId;
    this.orderLoadSucceeded = !this.editingOrderId;
    this.loadAvailableProducts();
    if (this.editingOrderId)
      this.loadOrder(this.editingOrderId);
  }
  ngAfterViewChecked(): void {
    this.initializeBootstrapPopover();
  }
  ngOnDestroy(): void {
    this.popoverInstances.forEach(instance => instance.dispose());
    this.popoverInstances.clear();
  }
  private initializeBootstrapPopover(): void {
    this.popoverInstances.forEach((instance, element) => {
      if (element.isConnected)
        return;
      instance.dispose();
      this.popoverInstances.delete(element);
    });
    const Popover = (window as any).bootstrap?.Popover;
    if (!Popover)
      return;
    this.elementRef.nativeElement
      .querySelectorAll('.create-order-page [data-order-shortcuts-popover]')
      .forEach(element => {
        if (this.popoverInstances.has(element))
          return;
        this.popoverInstances.set(element, new Popover(element, {
          container: 'body',
          customClass: 'order-shortcuts-popover'
        }));
      });
  }
  canDeactivate(): boolean {
    return !this.hasUnsavedChanges() || window.confirm(this.leaveConfirmationMessage);
  }
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges())
      return;
    event.preventDefault();
    event.returnValue = '';
  }
  handleToothSelected(event: ToothSelectionEvent): void {
    if (!this.canInteractWithOrder)
      return;
    const previousToothCount = this.currentToothCount;
    if (event.button === 1 || this.selectionMode === 'clear') {
      this.executeHistoryAction(() => {
        this.selectCurrentTooth(event);
        this.clearTooth(event.tooth);
      });
      return;
    }
    if ((event.shiftKey || this.selectionMode === 'range') && this.currentProduct) {
      const teeth = previousToothCount == null
        ? [event.tooth]
        : this.getTeethInRange(previousToothCount, event.count);
      this.executeHistoryAction(() => {
        this.selectCurrentTooth(event);
        this.applyCurrentProductToTeeth(teeth);
      });
      return;
    }
    if ((event.ctrlKey || this.selectionMode === 'single') && this.currentProduct) {
      this.executeHistoryAction(() => {
        this.selectCurrentTooth(event);
        this.applyCurrentProductToTeeth([event.tooth]);
      });
      return;
    }
    this.selectCurrentTooth(event);
    this.openProductPicker();
  }
  undo(): void {
    if (!this.canInteractWithOrder || !this.canUndo)
      return;
    const previous = this.undoStack.pop()!;
    this.redoStack.push(this.captureEditorSnapshot());
    this.restoreEditorSnapshot(previous);
  }
  redo(): void {
    if (!this.canInteractWithOrder || !this.canRedo)
      return;
    const next = this.redoStack.pop()!;
    this.undoStack.push(this.captureEditorSnapshot());
    this.restoreEditorSnapshot(next);
  }
  @HostListener('document:keydown', ['$event'])
  handleHistoryShortcut(event: KeyboardEvent): void {
    if (!this.canInteractWithOrder || (!event.ctrlKey && !event.metaKey) || this.isEditableTarget(event.target))
      return;
    const key = event.key.toLowerCase();
    const redoRequested = (key === 'z' && event.shiftKey) || key === 'y';
    const undoRequested = key === 'z' && !event.shiftKey;
    if (redoRequested && this.canRedo) {
      event.preventDefault();
      this.redo();
    }
    else if (undoRequested && this.canUndo) {
      event.preventDefault();
      this.undo();
    }
  }
  setSelectionMode(mode: SelectionMode): void {
    if (!this.canInteractWithOrder)
      return;
    this.selectionMode = mode;
  }
  openProductPicker(): void {
    if (!this.canInteractWithOrder)
      return;
    this.showModal('createOrderProductModal');
  }
  openFullConstructionPicker(): void {
    if (!this.canInteractWithOrder)
      return;
    this.showModal('createOrderFullConstructionModal');
  }
  openFilePicker(): void {
    if (!this.canInteractWithOrder)
      return;
    this.showModal('createOrderFileModal');
  }
  selectProductForCurrentTooth(product: ClientOrderProductModel): void {
    if (!this.canInteractWithOrder || this.currentTooth == null)
      return;
    this.executeHistoryAction(() => {
      const existing = this.selectedProducts.find(selected => selected.id === product.id
        && !selected.isFullConstruction
        && selected.teeth.includes(this.currentTooth!));
      if (existing)
        this.removeProductFromTooth(existing, this.currentTooth!);
      else
        this.applyProductToTeeth(product, [this.currentTooth!]);
      this.currentProduct = this.createCurrentProduct(product);
    });
    this.hideModal('createOrderProductModal');
  }
  isProductSelectedForCurrentTooth(product: ClientOrderProductModel): boolean {
    return this.currentTooth != null && this.selectedProducts.some(selected => selected.id === product.id
      && !selected.isFullConstruction
      && selected.teeth.includes(this.currentTooth!));
  }
  isFullConstructionSelected(product: ClientOrderProductModel): boolean {
    return this.selectedProducts.some(selected => selected.id === product.id && selected.isFullConstruction);
  }
  clearTooth(tooth: number): void {
    if (!this.canInteractWithOrder)
      return;
    this.executeHistoryAction(() => {
      this.removeToothFromSelectedProducts(tooth);
      this.syncSelectedDentalProducts();
    });
  }
  clearCurrentToothFromModal(): void {
    if (this.currentTooth != null) {
      this.clearTooth(this.currentTooth);
    }
    this.hideModal('createOrderProductModal');
  }
  clearEverything(): void {
    if (!this.canInteractWithOrder)
      return;
    this.executeHistoryAction(() => {
      this.selectedProducts = [];
      this.selectedDentalProducts = [];
      this.currentProduct = null;
      this.currentTooth = null;
      this.currentToothCount = null;
      this.selectionMode = 'choose';
      this.createOrderError = '';
    });
  }
  addFullConstruction(product: ClientOrderProductModel): void {
    if (!this.canInteractWithOrder)
      return;
    this.executeHistoryAction(() => {
      const existing = this.selectedProducts.find(p => p.id === product.id && p.isFullConstruction);
      if (!existing) {
        this.selectedProducts.push({
          id: product.id,
          name: product.name,
          color: product.color,
          teeth: [],
          quantity: 1,
          isFullConstruction: true
        });
      }
    });
    this.hideModal('createOrderFullConstructionModal');
  }
  incrementSelectedProduct(product: SelectedOrderProduct): void {
    if (!this.canInteractWithOrder || !product.isFullConstruction)
      return;
    this.executeHistoryAction(() => product.quantity += 1);
  }
  decrementSelectedProduct(product: SelectedOrderProduct): void {
    if (!this.canInteractWithOrder || !product.isFullConstruction)
      return;
    this.executeHistoryAction(() => {
      if (product.quantity > 1) {
        product.quantity -= 1;
      }
      else {
        this.removeSelectedProduct(product);
      }
    });
  }
  removeSelectedProduct(product: SelectedOrderProduct): void {
    if (!this.canInteractWithOrder)
      return;
    this.executeHistoryAction(() => {
      this.selectedProducts = this.selectedProducts.filter(p => p !== product);
      if (this.currentProduct?.id === product.id) {
        this.currentProduct = null;
        this.selectionMode = 'choose';
      }
      this.syncSelectedDentalProducts();
    });
  }
  toggleFullConstructionPainting(product: SelectedOrderProduct): void {
    if (!this.canInteractWithOrder || !product.isFullConstruction)
      return;
    if (this.isPaintingProduct(product)) {
      this.currentProduct = null;
      this.selectionMode = 'choose';
      return;
    }
    this.currentProduct = product;
    this.selectionMode = 'single';
  }
  isPaintingProduct(product: SelectedOrderProduct): boolean {
    return product.isFullConstruction
      && this.currentProduct?.id === product.id
      && this.currentProduct.isFullConstruction
      && (this.selectionMode === 'single' || this.selectionMode === 'range');
  }
  handleStagedFileSelected(fileUpload: PendingFileUpload): void {
    if (!this.canInteractWithOrder)
      return;
    this.addFiles([fileUpload]);
    this.hideModal('createOrderFileModal');
  }
  hideFilePicker(): void {
    this.hideModal('createOrderFileModal');
  }
  removeFile(index: number): void {
    if (!this.canInteractWithOrder)
      return;
    this.selectedFiles = this.selectedFiles.filter((_, fileIndex) => fileIndex !== index);
  }
  formatBytes(bytes?: number): string {
    if (!bytes || bytes <= 0)
      return '0 MB';
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(2)} MB`;
  }
  downloadFile(file: FileModel): void {
    if (!file.id || this.downloadingFileIds.has(file.id))
      return;
    this.createOrderError = '';
    this.downloadingFileIds.add(file.id);
    this.filesService.requestDownloadTicket(file.id).pipe(finalize(() => {
      this.downloadingFileIds.delete(file.id);
    })).subscribe({
      next: ({ ticket }) => this.filesService.startBrowserDownload(ticket),
      error: err => {
        console.error(err);
        this.createOrderError = getApiErrorMessage(err, 'Датотеката не може да се преземе.');
      }
    });
  }
  isDownloading(fileId: string): boolean {
    return this.downloadingFileIds.has(fileId);
  }
  submitCreateOrder(): void {
    if (this.creatingOrder)
      return;
    if (!this.canInteractWithOrder) {
      this.showSaveError(this.orderLoadFailed
        ? 'Налогот не беше вчитан и не може да се зачува. Вратете се назад и обидете се повторно.'
        : 'Почекајте налогот да се вчита.');
      return;
    }
    this.createOrderError = '';
    if (!this.selectedProducts.length) {
      this.showSaveError('Одберете барем една изработка.');
      return;
    }
    const request = this.buildRequest();
    const requestSignature = this.getRequestSignature(request);
    this.creatingOrder = true;
    const saveOrder = this.getSaveOrderRequest(request, requestSignature);
    saveOrder.pipe(switchMap(order => {
      this.persistedOrderId = order.id;
      this.lastSavedRequestSignature = requestSignature;
      return this.uploadPendingFiles(order);
    }), finalize(() => {
      this.creatingOrder = false;
    })).subscribe({
      next: (result) => {
        if (result.failedUploadCount > 0) {
          const summary = result.uploadedCount > 0
            ? `Налогот е зачуван. ${result.uploadedCount} датотеки се прикачени, но ${result.failedUploadCount} не успеаја.`
            : `Налогот е зачуван, но ${result.failedUploadCount} датотеки не се прикачија.`;
          this.showSaveError([summary, ...result.failureMessages].join('\n'));
          return;
        }
        this.orderSaved = true;
        this.router.navigate(['/orders'], { queryParams: { expandId: result.order.id } });
      },
      error: (err) => {
        console.error(err);
        this.showSaveError(getApiErrorMessage(err, 'Не може да се зачува налогот.'));
      }
    });
  }
  get regularProductGroups(): ProductCategoryGroup[] {
    return this.groupAvailableProducts(false);
  }
  get fullConstructionProductGroups(): ProductCategoryGroup[] {
    return this.groupAvailableProducts(true);
  }
  get activeProductName(): string {
    return this.currentProduct?.name ?? 'Нема избрана';
  }
  get pageTitle(): string {
    return this.editingOrderId ? 'Уреди налог' : 'Додади налог';
  }
  get canInteractWithOrder(): boolean {
    return !this.editingOrderId
      || (!this.loadingOrder && this.orderLoadSucceeded && !this.orderLoadFailed);
  }
  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
  private hasUnsavedChanges(): boolean {
    if (this.orderSaved)
      return false;
    if (this.persistedOrderId && this.lastSavedRequestSignature != null) {
      return this.getRequestSignature(this.buildRequest()) !== this.lastSavedRequestSignature
        || this.selectedFiles.length > 0;
    }
    return Boolean(this.createOrderForm.patientName.trim()
      || this.createOrderForm.description.trim()
      || this.createOrderForm.toothColor != null
      || this.selectedProducts.length
      || this.selectedFiles.length);
  }
  private buildRequest(): CreateOrderRequest {
    return {
      patientName: this.createOrderForm.patientName,
      description: this.createOrderForm.description,
      toothColor: this.createOrderForm.toothColor,
      selectedTeethData: JSON.stringify(this.selectedProducts)
    };
  }
  private getRequestSignature(request: CreateOrderRequest): string {
    return JSON.stringify(request);
  }
  private getSaveOrderRequest(request: CreateOrderRequest, requestSignature: string): Observable<OrderModel> {
    if (this.persistedOrderId && this.lastSavedRequestSignature === requestSignature) {
      return of({ id: this.persistedOrderId } as OrderModel);
    }
    const saveOrder = this.persistedOrderId
      ? this.orderService.updateOrder(this.persistedOrderId, request)
      : this.orderService.createOrder(request);
    return saveOrder.pipe(tap(order => {
      this.persistedOrderId = order.id;
      this.lastSavedRequestSignature = requestSignature;
    }));
  }
  private uploadPendingFiles(order: OrderModel): Observable<OrderSaveResult> {
    const pendingFiles = [...this.selectedFiles];
    if (!pendingFiles.length) {
      return of({ order, failedUploadCount: 0, uploadedCount: 0, failureMessages: [] });
    }
    return forkJoin(pendingFiles.map(fileUpload => this.filesService.uploadFileForOrder(order.id, fileUpload.file, fileUpload.description).pipe(map(() => ({ fileUpload, succeeded: true, errorMessage: null as string | null })), catchError(error => {
      console.error(error);
      return of({
        fileUpload,
        succeeded: false,
        errorMessage: getApiErrorMessage(error, 'Датотеката не може да се прикачи.')
      });
    })))).pipe(map(results => {
      const uploadedKeys = new Set(results.filter(result => result.succeeded).map(result => this.getFileKey(result.fileUpload.file)));
      this.selectedFiles = this.selectedFiles.filter(fileUpload => !uploadedKeys.has(this.getFileKey(fileUpload.file)));
      return {
        order,
        failedUploadCount: results.length - uploadedKeys.size,
        uploadedCount: uploadedKeys.size,
        failureMessages: [...new Set(results
          .map(result => result.errorMessage)
          .filter((message): message is string => !!message))]
      };
    }));
  }
  private loadAvailableProducts(): void {
    this.loadingProducts = true;
    this.orderService.getAvailableProducts().subscribe({
      next: (products) => {
        this.availableProducts = products ?? [];
        this.loadingProducts = false;
      },
      error: (err) => {
        console.error(err);
        this.createOrderError = getApiErrorMessage(err, 'Не може да се вчитаат изработките.');
        this.loadingProducts = false;
      }
    });
  }
  private loadOrder(id: string): void {
    this.loadingOrder = true;
    this.orderLoadFailed = false;
    this.orderLoadSucceeded = false;
    this.orderService.getOrder(id).pipe(finalize(() => this.loadingOrder = false)).subscribe({
      next: order => {
        this.existingFiles = order.files ?? [];
        this.createOrderForm = {
          patientName: order.patientName ?? '',
          description: order.description ?? '',
          toothColor: order.toothColor
        };
        const products = new Map<string, SelectedOrderProduct>();
        order.products.forEach(item => {
          const key = `${item.id}-${!!item.isFullConstruction}`;
          let selected = products.get(key);
          if (!selected) {
            selected = {
              id: item.id,
              name: item.name,
              color: item.color,
              teeth: [],
              quantity: 0,
              isFullConstruction: !!item.isFullConstruction
            };
            products.set(key, selected);
          }
          if (Number(item.tooth) > 0 && !selected.teeth.includes(Number(item.tooth))) {
            selected.teeth.push(Number(item.tooth));
          }
          selected.quantity = selected.isFullConstruction
            ? selected.quantity + Number(item.quantity)
            : selected.teeth.length;
        });
        this.selectedProducts = Array.from(products.values()).map(product => ({
          ...product,
          teeth: this.sortTeeth(product.teeth),
          quantity: Math.max(1, product.quantity)
        }));
        this.syncSelectedDentalProducts();
        this.resetHistory();
        this.lastSavedRequestSignature = this.getRequestSignature(this.buildRequest());
        this.orderLoadSucceeded = true;
      },
      error: err => {
        console.error(err);
        this.orderLoadFailed = true;
        this.createOrderError = getApiErrorMessage(err, 'Налогот не може да се вчита.');
      }
    });
  }
  private applyCurrentProductToTeeth(teeth: number[]): void {
    if (!this.currentProduct)
      return;
    this.applyProductToTeeth({
      id: this.currentProduct.id,
      name: this.currentProduct.name,
      color: this.currentProduct.color,
      categoryName: '',
      categoryColor: this.currentProduct.color,
      isFullConstruction: this.currentProduct.isFullConstruction
    }, teeth);
  }
  private applyProductToTeeth(product: ClientOrderProductModel, teeth: number[]): void {
    const uniqueTeeth = Array.from(new Set(teeth));
    uniqueTeeth.forEach(tooth => {
      const existing = this.selectedProducts.find(p => p.id === product.id && p.isFullConstruction === product.isFullConstruction);
      if (existing) {
        if (!existing.teeth.includes(tooth))
          existing.teeth = this.sortTeeth([...existing.teeth, tooth]);
        if (!existing.isFullConstruction)
          existing.quantity = existing.teeth.length;
      }
      else {
        this.selectedProducts.push({
          id: product.id,
          name: product.name.trim(),
          color: product.color,
          teeth: [tooth],
          quantity: 1,
          isFullConstruction: product.isFullConstruction
        });
      }
    });
    this.syncSelectedDentalProducts();
  }
  private createCurrentProduct(product: ClientOrderProductModel): SelectedOrderProduct {
    return {
      id: product.id,
      name: product.name.trim(),
      color: product.color,
      teeth: [],
      quantity: 1,
      isFullConstruction: false
    };
  }
  private removeToothFromSelectedProducts(tooth: number): void {
    this.selectedProducts.forEach(product => {
      product.teeth = product.teeth.filter(selectedTooth => selectedTooth !== tooth);
      if (!product.isFullConstruction)
        product.quantity = product.teeth.length;
    });
    this.selectedProducts = this.selectedProducts.filter(product => product.isFullConstruction || product.teeth.length > 0);
  }
  private removeProductFromTooth(product: SelectedOrderProduct, tooth: number): void {
    product.teeth = product.teeth.filter(selectedTooth => selectedTooth !== tooth);
    if (!product.isFullConstruction)
      product.quantity = product.teeth.length;
    this.selectedProducts = this.selectedProducts.filter(selected => selected.isFullConstruction || selected.teeth.length > 0);
    this.syncSelectedDentalProducts();
  }
  private syncSelectedDentalProducts(): void {
    this.selectedDentalProducts = this.selectedProducts
      .flatMap(product => product.teeth.map(tooth => ({
        id: product.id,
        name: product.name,
        color: product.color,
        tooth,
        quantity: product.isFullConstruction ? product.quantity : 1,
        isFullConstruction: product.isFullConstruction
      })));
  }
  private executeHistoryAction(action: () => void): void {
    const isRootAction = this.historyActionDepth === 0;
    const before = isRootAction ? this.captureEditorSnapshot() : null;
    this.historyActionDepth += 1;
    try {
      action();
    }
    finally {
      this.historyActionDepth -= 1;
    }
    if (!before || !this.hasProductSelectionChanged(before.selectedProducts, this.selectedProducts))
      return;
    this.undoStack.push(before);
    if (this.undoStack.length > MAX_HISTORY_LENGTH)
      this.undoStack.shift();
    this.redoStack = [];
  }
  private captureEditorSnapshot(): OrderEditorSnapshot {
    return {
      selectedProducts: this.cloneSelectedProducts(this.selectedProducts),
      currentProduct: this.currentProduct ? this.cloneSelectedProduct(this.currentProduct) : null,
      currentTooth: this.currentTooth,
      currentToothCount: this.currentToothCount,
      selectionMode: this.selectionMode
    };
  }
  private restoreEditorSnapshot(snapshot: OrderEditorSnapshot): void {
    this.selectedProducts = this.cloneSelectedProducts(snapshot.selectedProducts);
    this.currentProduct = snapshot.currentProduct ? this.cloneSelectedProduct(snapshot.currentProduct) : null;
    this.currentTooth = snapshot.currentTooth;
    this.currentToothCount = snapshot.currentToothCount;
    this.selectionMode = snapshot.selectionMode;
    this.createOrderError = '';
    this.syncSelectedDentalProducts();
  }
  private cloneSelectedProducts(products: SelectedOrderProduct[]): SelectedOrderProduct[] {
    return products.map(product => this.cloneSelectedProduct(product));
  }
  private cloneSelectedProduct(product: SelectedOrderProduct): SelectedOrderProduct {
    return { ...product, teeth: [...product.teeth] };
  }
  private hasProductSelectionChanged(before: SelectedOrderProduct[], after: SelectedOrderProduct[]): boolean {
    return JSON.stringify(before) !== JSON.stringify(after);
  }
  private resetHistory(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
  private selectCurrentTooth(event: ToothSelectionEvent): void {
    this.currentTooth = event.tooth;
    this.currentToothCount = event.count;
    this.createOrderError = '';
  }
  private isEditableTarget(target: EventTarget | null): boolean {
    return target instanceof Element
      && !!target.closest('input, textarea, select, [contenteditable="true"]');
  }
  private showSaveError(message: string): void {
    this.createOrderError = message;
    setTimeout(() => {
      const errorElement = this.elementRef.nativeElement.querySelector<HTMLElement>('.create-order-error');
      if (!errorElement)
        return;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      errorElement.focus({ preventScroll: true });
    });
  }
  private getTeethInRange(startCount: number, endCount: number): number[] {
    const start = Math.min(startCount, endCount);
    const end = Math.max(startCount, endCount);
    return TOOTH_ORDER_BY_COUNT.slice(start - 1, end);
  }
  private sortTeeth(teeth: number[]): number[] {
    return teeth.sort((a, b) => (TOOTH_COUNT_BY_TOOTH.get(a) ?? a) - (TOOTH_COUNT_BY_TOOTH.get(b) ?? b));
  }
  private groupAvailableProducts(isFullConstruction: boolean): ProductCategoryGroup[] {
    const groups = new Map<string, ProductCategoryGroup>();
    this.availableProducts
      .filter(product => product.isFullConstruction === isFullConstruction)
      .forEach(product => {
        const name = product.categoryName || 'Останати';
        const color = product.categoryColor || '#2e8bc0';
        if (!groups.has(name)) {
          groups.set(name, { name, color, products: [] });
        }
        groups.get(name)?.products.push(product);
      });
    return Array.from(groups.values());
  }
  private addFiles(fileUploads: PendingFileUpload[]): void {
    if (!fileUploads.length)
      return;
    const validFiles = fileUploads.filter(fileUpload => fileUpload.file.size > 0 && fileUpload.file.size <= MAX_FILE_UPLOAD_SIZE_BYTES);
    if (validFiles.length !== fileUploads.length) {
      this.createOrderError = 'Се поддржуваат непразни датотеки до 200 MB.';
    }
    const existingKeys = new Set(this.selectedFiles.map(fileUpload => this.getFileKey(fileUpload.file)));
    const newFiles = validFiles.filter(fileUpload => !existingKeys.has(this.getFileKey(fileUpload.file)));
    this.selectedFiles = [...this.selectedFiles, ...newFiles];
  }
  private getFileKey(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }
  private showModal(id: string): void {
    const modalEl = document.getElementById(id);
    const bootstrap = (window as any).bootstrap;
    if (!modalEl || !bootstrap?.Modal)
      return;
    const instance = bootstrap.Modal.getInstance(modalEl) ?? new bootstrap.Modal(modalEl);
    instance.show();
  }
  private hideModal(id: string): void {
    const modalEl = document.getElementById(id);
    const bootstrap = (window as any).bootstrap;
    if (!modalEl || !bootstrap?.Modal)
      return;
    const instance = bootstrap.Modal.getInstance(modalEl) ?? new bootstrap.Modal(modalEl);
    instance.hide();
  }
}
